<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import hash from 'hash.js'
import { colors } from '../utils/chart'

///////////////////////////////////////////////////////////////////////////////

function textToRandomNumber(text: string, min: number, max: number): number {
  const hashHex = hash.sha256().update(text).digest('hex')
  const integerHash = BigInt('0x' + hashHex)
  const rangeSize = BigInt(max - min + 1)
  const randomNumber = min + Number(integerHash % rangeSize)

  return randomNumber
}

///////////////////////////////////////////////////////////////////////////////

const props = withDefaults(
  defineProps<{
    image?: string | null
    initials?: string
    // Company/person name; initials are derived from it when the initials
    // prop is not given (first letters of the first two words, e.g.
    // "Space Exploration Technologies" -> "SE").
    name?: string | null
    size?: string
    // 'circle' — person/investor avatar with a stable pastel from the
    // initials hash. 'square' — same pastel treatment on a rounded-md
    // square (list rows). 'tile' — the dark slate company tile
    // (investment-summary treatment), monochrome.
    variant?: 'circle' | 'square' | 'tile'
  }>(),
  {
    image: null,
    initials: '',
    name: null,
    size: 'sm',
    variant: 'circle',
  },
)

const resolvedInitials = computed(() => {
  if (props.initials) return props.initials
  const words = (props.name ?? '').trim().split(/\s+/).filter(Boolean)
  if (!words.length) return ''
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
})

// Fall back to the initials when the image asset is missing or 40s.
const imgFailed = ref(false)
watch(
  () => props.image,
  () => {
    imgFailed.value = false
  },
)
const showImage = computed(() => !!props.image && !imgFailed.value)

// prettier-ignore
const variants = {
  'circle': 'rounded-full !no-underline shadow-sm ring-1 ring-inset ring-black/5 hover:shadow-sm hover:scale-105',
  'square': 'rounded-md shadow-sm ring-1 ring-inset ring-black/5',
  'tile': 'rounded-lg bg-slate-800 text-white',
}

// prettier-ignore
const sizes = {
  'xs': 'h-4 w-4 text-[7px] font-bold tracking-wide text-white',
  'sm': 'h-6 w-6 text-[9px] font-bold tracking-wide text-white',
  'base': 'h-7 w-7 text-[10px] font-bold tracking-wide text-white',
  '32': 'h-8 w-8 text-[11px] font-bold tracking-wide text-white',
  'md': 'h-9 w-9 text-[12px] font-bold tracking-wide text-white',
  'lg': 'h-28 w-28 text-4xl font-medium tracking-wide text-white',
  'xl': 'h-12 w-12 text-sm font-bold text-white',
  '2xl': 'h-16 w-16 text-xl font-bold text-white',
}

const rand = computed(() => {
  const min = 0
  const max = colors.length - 1
  return textToRandomNumber(resolvedInitials.value, min, max)
})

const randomColor = computed(() => colors[rand.value])
</script>

<template>
  <div
    class="flex cursor-default items-center justify-center overflow-hidden"
    :class="[variants[variant], sizes[size]]"
    :style="{ background: showImage ? 'white' : variant === 'tile' ? undefined : randomColor }"
  >
    <img
      v-if="showImage"
      :src="image!"
      :alt="resolvedInitials"
      loading="lazy"
      decoding="async"
      class="h-full w-full"
      :class="variant === 'tile' ? 'object-contain' : 'object-cover'"
      @error="imgFailed = true"
    />
    <div v-else>{{ resolvedInitials }}</div>
  </div>
</template>
